import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserTimezone, COMMON_TIMEZONES, isValidTimezone } from '../utils/timezoneUtils';
import { logger } from '../utils/logger';

interface TimezoneContextType {
    userTimezone: string;
    setUserTimezone: (timezone: string) => void;
    availableTimezones: typeof COMMON_TIMEZONES;
    isTimezoneDetected: boolean;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

interface TimezoneProviderProps {
    children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
    const [userTimezone, setUserTimezoneState] = useState<string>('');
    const [isTimezoneDetected, setIsTimezoneDetected] = useState(false);

    // Initialize timezone on mount
    useEffect(() => {
        const initializeTimezone = () => {
            try {
                // Try to get timezone from localStorage first
                const savedTimezone = localStorage.getItem('userTimezone');

                if (savedTimezone && isValidTimezone(savedTimezone)) {
                    setUserTimezoneState(savedTimezone);
                    setIsTimezoneDetected(true);
                } else {
                    // Fallback to browser detection
                    const browserTimezone = getUserTimezone();
                    setUserTimezoneState(browserTimezone);
                    setIsTimezoneDetected(true);

                    // Save to localStorage for future use
                    localStorage.setItem('userTimezone', browserTimezone);
                }
            } catch {
                // Fallback to UTC
                setUserTimezoneState('UTC');
                setIsTimezoneDetected(true);
            }
        };

        initializeTimezone();
    }, []);

    const setUserTimezone = (timezone: string) => {
        if (isValidTimezone(timezone)) {
            setUserTimezoneState(timezone);
            localStorage.setItem('userTimezone', timezone);
        } else {
            logger.error('Invalid timezone:', timezone);
        }
    };

    const value: TimezoneContextType = {
        userTimezone,
        setUserTimezone,
        availableTimezones: COMMON_TIMEZONES,
        isTimezoneDetected,
    };

    return (
        <TimezoneContext.Provider value={value}>
            {children}
        </TimezoneContext.Provider>
    );
};

export const useTimezone = (): TimezoneContextType => {
    const context = useContext(TimezoneContext);
    if (context === undefined) {
        throw new Error('useTimezone must be used within a TimezoneProvider');
    }
    return context;
};
